import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container,
  Paper,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,

  Switch,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Collapse,
  Grid,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  Alert,
  Button,
  Snackbar,
  CircularProgress,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Card,
  CardContent,
  Stack,
  MenuItem
} from '@mui/material';
import {
  Save as SaveIcon,
  OpenInNew as OpenInNewIcon,
  Policy as PolicyIcon,
  Gavel as GavelIcon,
  PersonRemove as PersonRemoveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ListAlt as ListAltIcon
} from '@mui/icons-material';
import {
  DarkMode as DarkModeIcon,
  Visibility as VisibilityIcon,
  NotificationsActive as SoundIcon,
  Schedule as ScheduleIcon,
  DirectionsCar as RidesIcon,
  Euro as EuroIcon,
  Event as CalendarIcon,
  AllInclusive as InfinityIcon,
  QrCode2 as QrCodeIcon,
  Apple as AppleIcon,
  Phone as PhoneIcon,
  PhoneForwarded as PhoneForwardedIcon,
  Shop as PlayStoreIcon,
  LocalTaxi as TaxiIcon,
  LocalHospital as MedicalIcon,
  NightsStay as NightIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ExitToApp as AutoLogoutIcon,
  Map as MapIcon,
  NearMe as AnfahrtIcon,
  AltRoute as AltRouteIcon,
  Traffic as TrafficIcon,
  NoTransfer as AvoidIcon,
  AutoAwesome as AutoFollowUpIcon,
  WarningAmber as WarningIcon,
  SubdirectoryArrowRight as SubOptionIcon
} from '@mui/icons-material';
import MobileAppQRDialog from './MobileAppQRDialog';
import WebPushSettingsSection from './WebPushSettingsSection';
import NotificationPreferencesSection from './NotificationPreferencesSection';
import TelephonyConnectorSettingsSection from './TelephonyConnectorSettingsSection';
import AppPaymentSettingsSection from './AppPaymentSettingsSection';
import ReceiptSettingsSection from './ReceiptSettingsSection';
import AiTelephonySettingsSection from './AiTelephonySettingsSection';
import PageHeader from '../common/PageHeader';
import packageJson from '../../../package.json';
import {
  fetchSettings,
  saveSettings,
  saveSettingImmediately,
  saveSettingsImmediately,
  updateLocalSettings,
  loadLocalSettings,
  fetchCloudFunctionSettings,
  saveCloudFunctionSetting
} from '../../redux/slices/settingsSlice';
import {
  fetchFestpreise,
  addFestpreis,
  updateFestpreis,
  deleteFestpreis
} from '../../redux/slices/festpreiseSlice';
import { fetchSectors } from '../../redux/slices/sectorsSlice';
import { selectCanManageGlobalSettings } from '../../redux/slices/authSlice';
import { APIProvider } from '@vis.gl/react-google-maps';
import PlaceAutocomplete from '../common/PlaceAutocomplete';
import brandingConfig from '../../config/brandingConfig';

const { version } = packageJson;
const GOOGLE_MAPS_SCRIPT_ID = 'google-maps-js-api';
const SERVICE_AREA_RADIUS_METERS = 10000;
const SERVICE_AREA_OUTSIDE_BUFFER_MINUTES = 5;
const SERVICE_AREA_OUTSIDE_FALLBACK_MINUTES = 30;
const SERVICE_AREA_MAX_LEAD_MINUTES = 120;

const loadGoogleMapsScript = () => new Promise((resolve, reject) => {
  if (window.google?.maps?.Geocoder) {
    resolve(window.google);
    return;
  }

  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (!apiKey) {
    reject(new Error('Google Maps API Key fehlt'));
    return;
  }

  const existingScript = document.getElementById(GOOGLE_MAPS_SCRIPT_ID);
  if (existingScript) {
    existingScript.addEventListener('load', () => resolve(window.google), { once: true });
    existingScript.addEventListener('error', reject, { once: true });
    return;
  }

  const script = document.createElement('script');
  script.id = GOOGLE_MAPS_SCRIPT_ID;
  script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
  script.async = true;
  script.defer = true;
  script.onload = () => resolve(window.google);
  script.onerror = reject;
  document.head.appendChild(script);
});

const geocodeAddress = async (address) => {
  if (!address?.trim()) return null;

  await loadGoogleMapsScript();
  const geocoder = new window.google.maps.Geocoder();

  return new Promise((resolve) => {
    geocoder.geocode({ address, componentRestrictions: { country: 'DE' } }, (results, status) => {
      if (status !== 'OK' || !results?.[0]?.geometry?.location) {
        // Status protokollieren – haeufigste Ursache: 'REQUEST_DENIED', wenn die
        // Geocoding API fuer den Maps-API-Key nicht freigeschaltet ist.
        console.warn(`Geocoding fehlgeschlagen (status=${status}) fuer Adresse: ${address}`);
        resolve(null);
        return;
      }

      const result = results[0];
      resolve({
        address: result.formatted_address || address,
        lat: result.geometry.location.lat(),
        lng: result.geometry.location.lng()
      });
    });
  });
};

/**
 * SettingsPage-Komponente
 *
 * Ermöglicht die Konfiguration verschiedener App-Einstellungen.
 * Globale Einstellungen werden in Firebase gespeichert.
 * Lokale Einstellungen werden in localStorage gespeichert.
 */
// Klappbare Gruppe für die Einstellungsseite (einheitliche Akkordeon-Optik).
const SettingsGroup = ({ title, defaultExpanded = false, children }) => (
  <Accordion
    defaultExpanded={defaultExpanded}
    disableGutters
    elevation={0}
    square
    sx={{ '&:before': { display: 'none' }, borderTop: 1, borderColor: 'divider' }}
  >
    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: 'grey.200' }}>
      <Typography variant="subtitle1" fontWeight="bold">{title}</Typography>
    </AccordionSummary>
    <AccordionDetails sx={{ p: 0 }}>
      {children}
    </AccordionDetails>
  </Accordion>
);

const SettingsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux State
  const {
    global: globalSettings,
    cloudFunctions: cloudFunctionSettings,
    local: localSettings,
    isLoading,
    isSaving,
    isInitialized
  } = useSelector((state) => state.settings);

  // Lokaler State für Formular (um Änderungen zu tracken bevor gespeichert wird)
  const [formState, setFormState] = useState({
    // Treueprogramm-Modus: 'off' | 'stamp' | 'miles' (genau eines aktiv)
    loyaltyProgramMode: 'off',
    // Globale Einstellungen (Treuekarte, System 1)
    loyaltyCardActive: false,
    loyaltyCardRidesRequired: 5,
    loyaltyCardDiscountEur: 5,
    loyaltyCardEndDate: '',
    loyaltyCardNoEndDate: true,
    // Treuemeilen (System 2)
    milesPerKm: 1,
    milesPerBlock: 50,
    milesBlockEur: 5,
    milesEndDate: '',
    milesNoEndDate: true,
    // Mobile App Store Links
    appStoreUrl: '',
    playStoreUrl: '',
    // Zentrale Telefonnummer (Anruf-Fallback in der Kunden-App)
    centralPhoneNumber: '',
    // Tarif-Einstellungen
    tarifGrundpreis: 3.5,
    tarifPreisProKm: 2.5,
    tarifGrossraumzuschlag: 5.0,
    // Nachttarif-Einstellungen (Aktivieren-Schalter speichert sofort -> nicht im Form-State)
    nachtTarifGrundpreis: 3.5,
    nachtTarifPreisProKm: 2.5,
    nachtTarifWocheStart: '22:00',
    nachtTarifWocheEnd: '06:00',
    nachtTarifWochenendStart: '20:00',
    nachtTarifWochenendEnd: '07:00',
    // Anfahrtskosten (außerhalb Betriebsgebiet); Schalter speichert sofort -> nicht im Form-State
    anfahrtPreisProKm: 0.5
  });

  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [dispatchMethodConfirm, setDispatchMethodConfirm] = useState(null);

  // Festpreise State
  const festpreiseState = useSelector((state) => state.festpreise);
  const sectors = useSelector((state) => state.sectors?.sectors || []);
  const canManageGlobalSettings = useSelector(selectCanManageGlobalSettings);
  const sofortbestellungAnzeigeMethode =
    globalSettings.sofortbestellungAnzeigeMethode === 'freimeldung'
      ? 'sectors'
      : (globalSettings.sofortbestellungAnzeigeMethode || 'distance');
  const isSectorDispatchActive = sofortbestellungAnzeigeMethode === 'sectors';
  const serviceAreaMode = globalSettings.serviceAreaEnabled === true
    ? (globalSettings.serviceAreaMode === 'polygon' ? 'polygon' : 'radius')
    : (globalSettings.serviceAreaMode || 'disabled');
  const serviceAreaPolygons = Array.isArray(globalSettings.serviceAreaPolygons)
    ? globalSettings.serviceAreaPolygons
    : [];
  const hasServiceAreaPolygon = serviceAreaPolygons.some((polygon) => {
    const points = polygon?.points || polygon?.polygon || [];
    return polygon?.active !== false && Array.isArray(points) && points.length >= 3;
  });
  const activeSectorCount = sectors.filter(sector => sector.active !== false).length;
  const [festpreisDialogOpen, setFestpreisDialogOpen] = useState(false);
  const [festpreiseExpanded, setFestpreiseExpanded] = useState(false);
  const [editingFestpreis, setEditingFestpreis] = useState(null);
  const [festpreisForm, setFestpreisForm] = useState({
    start: '', startAdresse: '', ziel: '', zielAdresse: '', preis: '', aktiv: true, festAdresseModus: 'ziel'
  });

  // Lade Einstellungen beim Mount
  useEffect(() => {
    dispatch(fetchSettings());
    dispatch(loadLocalSettings());
    dispatch(fetchFestpreise());
    dispatch(fetchCloudFunctionSettings());
    dispatch(fetchSectors());
  }, [dispatch]);

  useEffect(() => {
    if (canManageGlobalSettings && isInitialized && globalSettings.sofortbestellungAnzeigeMethode === 'freimeldung') {
      dispatch(saveSettingImmediately({ key: 'sofortbestellungAnzeigeMethode', value: 'sectors' }));
    }
  }, [canManageGlobalSettings, dispatch, isInitialized, globalSettings.sofortbestellungAnzeigeMethode]);

  const dispatchMethodLabel = (method) =>
    method === 'sectors' ? 'Nach Sektoren' : 'Entfernungsbasiert';

  const handleDispatchMethodChangeRequest = (nextMethod) => {
    if (nextMethod === sofortbestellungAnzeigeMethode) return;
    setDispatchMethodConfirm({
      previousMethod: sofortbestellungAnzeigeMethode,
      nextMethod
    });
  };

  const handleConfirmDispatchMethodChange = async () => {
    if (!dispatchMethodConfirm) return;
    try {
      await dispatch(saveSettingImmediately({
        key: 'sofortbestellungAnzeigeMethode',
        value: dispatchMethodConfirm.nextMethod
      })).unwrap();
      setSnackbar({
        open: true,
        message: 'Anzeige-Methode wurde gespeichert. Alle Fahrer werden abgemeldet und muessen sich neu anmelden.',
        severity: 'success'
      });
      setDispatchMethodConfirm(null);
    } catch (error) {
      setSnackbar({ open: true, message: `Fehler beim Speichern: ${error}`, severity: 'error' });
    }
  };

  const handleServiceAreaToggle = async () => {
    const nextEnabled = globalSettings.serviceAreaEnabled !== true;
    const headquartersAddress = brandingConfig.address || '';

    try {
      if (!nextEnabled) {
        await dispatch(saveSettingsImmediately({
          values: {
            serviceAreaEnabled: false,
            serviceAreaMode: 'disabled'
          }
        })).unwrap();
        setSnackbar({
          open: true,
          message: 'Betriebsgebiet wurde deaktiviert',
          severity: 'success'
        });
        return;
      }

      // WICHTIG: Zuerst geokodieren – das Betriebsgebiet darf erst dann
      // aktiviert werden, wenn der Mittelpunkt (Koordinaten) feststeht.
      // Sonst feuert die Cloud Function `onServiceAreaSettingsChanged` auf
      // einem halb-konfigurierten Zustand und gibt Vorbestellungen mit dem
      // pauschalen Aussenbereich-Vorlauf (30 Min) sofort frei.
      let geocoded = null;
      try {
        geocoded = await geocodeAddress(headquartersAddress);
      } catch (error) {
        console.warn('Betriebszentrale konnte nicht automatisch geokodiert werden:', error);
      }

      if (!geocoded) {
        // Fehlschlag: NICHT aktivieren, damit kein unkonfiguriertes
        // Betriebsgebiet zurueckbleibt. Haeufigste Ursache: Geocoding API
        // ist fuer den Google-Maps-API-Key nicht freigeschaltet.
        setSnackbar({
          open: true,
          message: 'Betriebsgebiet konnte nicht aktiviert werden: Die Koordinaten des Hauptsitzes ('
            + (headquartersAddress || 'keine Adresse im Impressum')
            + ') konnten nicht ermittelt werden. Bitte pruefen, ob die Geocoding API fuer den Maps-API-Key freigeschaltet ist.',
          severity: 'error'
        });
        return;
      }

      // Atomarer Write: enabled + mode + Adresse + Koordinaten in EINEM Update,
      // damit die Cloud Function nur mit vollstaendiger Konfiguration laeuft.
      await dispatch(saveSettingsImmediately({
        values: {
          serviceAreaEnabled: true,
          serviceAreaMode: 'radius',
          serviceAreaFallbackAddress: geocoded.address,
          serviceAreaFallbackLat: geocoded.lat,
          serviceAreaFallbackLng: geocoded.lng
        }
      })).unwrap();
      setSnackbar({
        open: true,
        message: 'Betriebsgebiet wurde aktiviert und der Hauptsitz aus dem Impressum übernommen',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({ open: true, message: `Fehler beim Speichern: ${error}`, severity: 'error' });
    }
  };

  // Synchronisiere Redux-State mit lokalem Formular-State
  useEffect(() => {
    if (isInitialized) {
      setFormState({
        loyaltyProgramMode: globalSettings.loyaltyProgramMode
          || (globalSettings.loyaltyCardActive ? 'stamp' : 'off'),
        loyaltyCardActive: globalSettings.loyaltyCardActive || false,
        loyaltyCardRidesRequired: globalSettings.loyaltyCardRidesRequired || 5,
        loyaltyCardDiscountEur: globalSettings.loyaltyCardDiscountEur || 5,
        loyaltyCardEndDate: globalSettings.loyaltyCardEndDate || '',
        loyaltyCardNoEndDate: globalSettings.loyaltyCardNoEndDate !== false,
        milesPerKm: globalSettings.milesPerKm ?? 1,
        milesPerBlock: globalSettings.milesPerBlock ?? 50,
        milesBlockEur: globalSettings.milesBlockEur ?? 5,
        milesEndDate: globalSettings.milesEndDate || '',
        milesNoEndDate: globalSettings.milesNoEndDate !== false,
        appStoreUrl: globalSettings.appStoreUrl || '',
        playStoreUrl: globalSettings.playStoreUrl || '',
        centralPhoneNumber: globalSettings.centralPhoneNumber || '',
        tarifGrundpreis: globalSettings.tarifGrundpreis ?? 3.5,
        tarifPreisProKm: globalSettings.tarifPreisProKm ?? 2.5,
        tarifGrossraumzuschlag: globalSettings.tarifGrossraumzuschlag ?? 5.0,
        nachtTarifGrundpreis: globalSettings.nachtTarifGrundpreis ?? 3.5,
        nachtTarifPreisProKm: globalSettings.nachtTarifPreisProKm ?? 2.5,
        nachtTarifWocheStart: globalSettings.nachtTarifWocheStart ?? '22:00',
        nachtTarifWocheEnd: globalSettings.nachtTarifWocheEnd ?? '06:00',
        nachtTarifWochenendStart: globalSettings.nachtTarifWochenendStart ?? '20:00',
        nachtTarifWochenendEnd: globalSettings.nachtTarifWochenendEnd ?? '07:00',
        anfahrtPreisProKm: globalSettings.anfahrtPreisProKm ?? 0.5
      });
    }
  }, [isInitialized, globalSettings]);

  // Handler für lokale Einstellungen (werden sofort gespeichert)
  const handleLocalToggle = (setting) => () => {
    dispatch(updateLocalSettings({
      [setting]: !localSettings[setting]
    }));
  };

  // App-Zahlungen (PayPal): Status aus AppPaymentSettingsSection — sperrt die
  // gekoppelten Schalter (Fahrpreis-Anzeige, Preiseingabe).
  const [appPaymentsEnabled, setAppPaymentsEnabled] = useState(false);
  const handleAppPaymentsEnabledChange = useCallback((isEnabled) => {
    setAppPaymentsEnabled(isEnabled);
  }, []);
  const lockedByAppPayments = 'Durch „Zahlung per App" festgelegt';

  // Quittungen/Rechnungen: Status aus ReceiptSettingsSection. Invariante
  // „Quittungen/Rechnungen ⇒ echte Fahrpreise" (Quittungsversand-Konzept 6.1,
  // Rechnungskunden-Konzept §1): Der Preiseingabe-Schalter wird — wie bei
  // App-Zahlungen — gesperrt, solange eine der Funktionen aktiv ist;
  // Ausschalten nur über deren Sektion.
  const [receiptsEnabled, setReceiptsEnabled] = useState(false);
  const handleReceiptsEnabledChange = useCallback((isEnabled) => {
    setReceiptsEnabled(isEnabled);
  }, []);
  const [invoicesEnabled, setInvoicesEnabled] = useState(false);
  const handleInvoicesEnabledChange = useCallback((isEnabled) => {
    setInvoicesEnabled(isEnabled);
  }, []);
  const fareLockSources = [
    appPaymentsEnabled && '„Zahlung per App"',
    receiptsEnabled && '„Quittungen"',
    invoicesEnabled && '„Rechnungen"'
  ].filter(Boolean);
  const fareInputLocked = fareLockSources.length > 0;
  const fareInputLockedHint = fareInputLocked
    ? `Durch ${fareLockSources.join(' und ')} festgelegt — es werden echte Fahrpreise benötigt. Zum Ändern zuerst die jeweilige Funktion deaktivieren.`
    : null;

  // „Fahrt automatisch abschließen" ist eine Unter-Option: nur sinnvoll,
  // wenn beim Abschluss KEINE Preisabfrage erscheint — sonst braucht der
  // Abschluss ohnehin den Preisdialog in der Fahrer-App.
  const fareInputActive =
    fareInputLocked ||
    globalSettings.requireDriverFareInput === true;

  // Handler für sofortiges Speichern einer globalen Einstellung
  const handleImmediateToggle = (setting, currentValue) => async () => {
    try {
      await dispatch(saveSettingImmediately({ key: setting, value: !currentValue })).unwrap();
      setSnackbar({
        open: true,
        message: 'Einstellung wurde gespeichert',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Fehler beim Speichern: ${error}`,
        severity: 'error'
      });
    }
  };

  const handleImmediateNumberSetting = (setting, min, max) => async (event) => {
    let value = parseInt(event.target.value, 10);
    if (isNaN(value)) value = min;
    if (value < min) value = min;
    if (value > max) value = max;

    try {
      await dispatch(saveSettingImmediately({ key: setting, value })).unwrap();
      setSnackbar({
        open: true,
        message: 'Einstellung wurde gespeichert',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Fehler beim Speichern: ${error}`,
        severity: 'error'
      });
    }
  };

  const handleImmediateValueSetting = (setting) => async (event) => {
    const value = event.target.value;

    try {
      await dispatch(saveSettingImmediately({ key: setting, value })).unwrap();
      setSnackbar({
        open: true,
        message: 'Einstellung wurde gespeichert',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Fehler beim Speichern: ${error}`,
        severity: 'error'
      });
    }
  };

  // Handler für Änderungen
  const handleChange = (setting) => (event) => {
    const value = event.target.type === 'checkbox'
      ? event.target.checked
      : event.target.value;

    setFormState(prev => ({
      ...prev,
      [setting]: value
    }));
  };

  // Handler für Zahlen-Eingaben
  const handleNumberChange = (setting, min, max) => (event) => {
    let value = parseInt(event.target.value, 10);
    if (isNaN(value)) value = min;
    if (value < min) value = min;
    if (value > max) value = max;

    setFormState(prev => ({
      ...prev,
      [setting]: value
    }));
  };

  // Handler für Dezimal-Eingaben (Tarif-Werte)
  const handleDecimalChange = (setting, min, max) => (event) => {
    let value = parseFloat(event.target.value);
    if (isNaN(value)) value = min;
    if (value < min) value = min;
    if (value > max) value = max;
    // Auf 2 Dezimalstellen runden
    value = Math.round(value * 100) / 100;

    setFormState(prev => ({
      ...prev,
      [setting]: value
    }));
  };

  // Berechnet die Tarif-Vorschau für eine Beispielstrecke
  const calculateFarePreview = (distanceKm, withZuschlag = false) => {
    let fare = formState.tarifGrundpreis + distanceKm * formState.tarifPreisProKm;
    if (withZuschlag) fare += formState.tarifGrossraumzuschlag;
    return (Math.round(fare * 10) / 10).toFixed(2);
  };

  // Berechnet die Nachttarif-Vorschau für eine Beispielstrecke
  const calculateNightFarePreview = (distanceKm, withZuschlag = false) => {
    let fare = formState.nachtTarifGrundpreis + distanceKm * formState.nachtTarifPreisProKm;
    if (withZuschlag) fare += formState.tarifGrossraumzuschlag;
    return (Math.round(fare * 10) / 10).toFixed(2);
  };

  // Speichern der Nachttarif-Einstellungen. Der Aktivieren-Schalter speichert
  // sofort (handleImmediateToggle): Der Button hier liegt IM Collapse-Bereich —
  // beim Ausschalten verschwaende er, der Aus-Zustand wäre nie speicherbar.
  const handleSaveNachtTarif = async () => {
    try {
      await dispatch(saveSettingImmediately({ key: 'nachtTarifGrundpreis', value: formState.nachtTarifGrundpreis })).unwrap();
      await dispatch(saveSettingImmediately({ key: 'nachtTarifPreisProKm', value: formState.nachtTarifPreisProKm })).unwrap();
      await dispatch(saveSettingImmediately({ key: 'nachtTarifWocheStart', value: formState.nachtTarifWocheStart })).unwrap();
      await dispatch(saveSettingImmediately({ key: 'nachtTarifWocheEnd', value: formState.nachtTarifWocheEnd })).unwrap();
      await dispatch(saveSettingImmediately({ key: 'nachtTarifWochenendStart', value: formState.nachtTarifWochenendStart })).unwrap();
      await dispatch(saveSettingImmediately({ key: 'nachtTarifWochenendEnd', value: formState.nachtTarifWochenendEnd })).unwrap();
      setSnackbar({
        open: true,
        message: 'Nachttarif-Einstellungen wurden gespeichert',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Fehler beim Speichern: ${error}`,
        severity: 'error'
      });
    }
  };

  // Speichern der Tarif-Einstellungen
  const handleSaveTarif = async () => {
    try {
      await dispatch(saveSettingImmediately({ key: 'tarifGrundpreis', value: formState.tarifGrundpreis })).unwrap();
      await dispatch(saveSettingImmediately({ key: 'tarifPreisProKm', value: formState.tarifPreisProKm })).unwrap();
      await dispatch(saveSettingImmediately({ key: 'tarifGrossraumzuschlag', value: formState.tarifGrossraumzuschlag })).unwrap();

      // Anfahrtspreis darf den Kilometerpreis nie übersteigen: beim Absenken
      // des Tarifs den Anfahrtspreis mit abkappen (Konzept anfahrtspreis_konzept.md §4).
      let anfahrtHint = '';
      if (formState.anfahrtPreisProKm > formState.tarifPreisProKm) {
        const capped = formState.tarifPreisProKm;
        await dispatch(saveSettingImmediately({ key: 'anfahrtPreisProKm', value: capped })).unwrap();
        setFormState(prev => ({ ...prev, anfahrtPreisProKm: capped }));
        anfahrtHint = ` — Anfahrtspreis auf ${capped.toFixed(2).replace('.', ',')} €/km reduziert (max. Kilometerpreis)`;
      }

      setSnackbar({
        open: true,
        message: `Tarif-Einstellungen wurden gespeichert${anfahrtHint}`,
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Fehler beim Speichern: ${error}`,
        severity: 'error'
      });
    }
  };

  // Speichern des Anfahrtspreises (der Aktivieren-Schalter speichert sofort;
  // Cap auf tarifPreisProKm, siehe anfahrtspreis_konzept.md §4).
  const handleSaveAnfahrt = async () => {
    try {
      const cappedPreis = Math.min(formState.anfahrtPreisProKm, formState.tarifPreisProKm);
      await dispatch(saveSettingImmediately({ key: 'anfahrtPreisProKm', value: cappedPreis })).unwrap();
      if (cappedPreis !== formState.anfahrtPreisProKm) {
        setFormState(prev => ({ ...prev, anfahrtPreisProKm: cappedPreis }));
      }
      setSnackbar({
        open: true,
        message: 'Anfahrtskosten-Einstellungen wurden gespeichert',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Fehler beim Speichern: ${error}`,
        severity: 'error'
      });
    }
  };

  // Speichern der globalen Einstellungen
  const handleSave = async () => {
    try {
      await dispatch(saveSettings({
        ...formState,
        showPreordersToAllDrivers: globalSettings.showPreordersToAllDrivers || false,
        autoAcceptPreBookings: globalSettings.autoAcceptPreBookings || false
      })).unwrap();
      setSnackbar({
        open: true,
        message: 'Treuekarten-Einstellungen wurden gespeichert',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Fehler beim Speichern: ${error}`,
        severity: 'error'
      });
    }
  };

  // Speichern der Mobile App Links
  const handleSaveMobileAppLinks = async () => {
    try {
      await dispatch(saveSettingImmediately({ key: 'appStoreUrl', value: formState.appStoreUrl })).unwrap();
      await dispatch(saveSettingImmediately({ key: 'playStoreUrl', value: formState.playStoreUrl })).unwrap();
      setSnackbar({
        open: true,
        message: 'App-Links wurden gespeichert',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Fehler beim Speichern: ${error}`,
        severity: 'error'
      });
    }
  };

  // Speichern der zentralen Telefonnummer (leer = Nummer aus dem Impressum übernehmen)
  const handleSaveCentralPhoneNumber = async () => {
    try {
      const value = (formState.centralPhoneNumber || '').trim() || brandingConfig.phone;
      await dispatch(saveSettingImmediately({ key: 'centralPhoneNumber', value })).unwrap();
      setFormState(prev => ({ ...prev, centralPhoneNumber: value }));
      setSnackbar({
        open: true,
        message: 'Zentrale Telefonnummer wurde gespeichert',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Fehler beim Speichern: ${error}`,
        severity: 'error'
      });
    }
  };

  // Festpreis Dialog Handler
  const handleOpenFestpreisDialog = (festpreis = null) => {
    if (festpreis) {
      setEditingFestpreis(festpreis);
      setFestpreisForm({
        start: festpreis.start || '',
        startAdresse: festpreis.startAdresse || '',
        ziel: festpreis.ziel || '',
        zielAdresse: festpreis.zielAdresse || '',
        preis: festpreis.preis ?? '',
        aktiv: festpreis.aktiv !== false,
        festAdresseModus: festpreis.festAdresseModus || 'ziel'
      });
    } else {
      setEditingFestpreis(null);
      setFestpreisForm({ start: '', startAdresse: '', ziel: '', zielAdresse: '', preis: '', aktiv: true, festAdresseModus: 'ziel' });
    }
    setFestpreisDialogOpen(true);
  };

  const handleSaveFestpreis = async () => {
    const data = {
      start: festpreisForm.start,
      ziel: festpreisForm.ziel,
      preis: parseFloat(festpreisForm.preis),
      aktiv: festpreisForm.aktiv,
      festAdresseModus: festpreisForm.festAdresseModus,
      ...(festpreisForm.startAdresse && { startAdresse: festpreisForm.startAdresse }),
      ...(festpreisForm.zielAdresse && { zielAdresse: festpreisForm.zielAdresse })
    };
    try {
      if (editingFestpreis) {
        await dispatch(updateFestpreis({ id: editingFestpreis.id, data })).unwrap();
      } else {
        await dispatch(addFestpreis(data)).unwrap();
      }
      setFestpreisDialogOpen(false);
      setSnackbar({ open: true, message: 'Festpreis wurde gespeichert', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: `Fehler: ${error}`, severity: 'error' });
    }
  };

  const handleDeleteFestpreis = async (id) => {
    try {
      await dispatch(deleteFestpreis(id)).unwrap();
      setSnackbar({ open: true, message: 'Festpreis wurde gelöscht', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: `Fehler: ${error}`, severity: 'error' });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Formatiere das minimale Datum (heute)
  const today = new Date().toISOString().split('T')[0];

  // Tatsächlich gespeicherter Treueprogramm-Modus (aus globalSettings), unabhängig
  // von der aktuell im Formular gewählten, evtl. noch nicht gespeicherten Option.
  const savedProgramMode = globalSettings.loyaltyProgramMode
    || (globalSettings.loyaltyCardActive ? 'stamp' : 'off');
  const savedProgramName = savedProgramMode === 'stamp'
    ? 'das Treuekarten-System'
    : savedProgramMode === 'miles'
      ? 'das Treuemeilen-System'
      : 'kein Programm';

  const breadcrumbs = [
    { label: 'Dashboard', path: '/dashboard' },
    { label: 'Einstellungen' }
  ];

  // Zeige Ladeindikator
  if (isLoading && !isInitialized) {
    return (
      <Container maxWidth="md">
        <PageHeader
          title="Einstellungen"
          breadcrumbs={breadcrumbs}
        />
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <PageHeader
        title="Einstellungen"
        breadcrumbs={breadcrumbs}
        description="Konfigurieren Sie die App-Einstellungen"
      />

      <Paper elevation={2} sx={{ p: 0 }}>
        {!canManageGlobalSettings && (
          <Alert severity="info" sx={{ m: 2 }}>
            Diese Einstellungen werden zentral von Taxiunion Pinneberg verwaltet. Ihr Unternehmer-Konto kann die Werte einsehen, aber nicht aendern.
          </Alert>
        )}

        <SettingsGroup title="Telefonie & KI">
          <TelephonyConnectorSettingsSection />
          <Divider />
          <AiTelephonySettingsSection canManage={canManageGlobalSettings} />
        </SettingsGroup>

        <SettingsGroup title="Anzeige & Gerät">
          <WebPushSettingsSection />
          <Divider />
          <NotificationPreferencesSection />
          <Divider />
          {/* Abschnitt: Darstellung */}
          <Box sx={{ p: 2, bgcolor: 'grey.100' }}>
            <Typography variant="subtitle1" fontWeight="bold">
              Darstellung
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Lokale Einstellung (nur dieses Gerät)
            </Typography>
          </Box>
          <List disablePadding>
            <ListItem
              secondaryAction={
                <Switch
                  edge="end"
                  checked={localSettings.darkMode}
                  onChange={handleLocalToggle('darkMode')}
                  color="primary"
                />
              }
            >
              <ListItemIcon>
                <DarkModeIcon />
              </ListItemIcon>
              <ListItemText
                primary="Nachtmodus"
                secondary="Dunkles Farbschema für die Anwendung aktivieren"
              />
            </ListItem>
          </List>

          <Divider />

          {/* Ton-Benachrichtigungen (lokal, nur dieses Gerät) */}
          <Box sx={{ p: 2, bgcolor: 'grey.100' }}>
            <Typography variant="subtitle1" fontWeight="bold">
              Ton-Benachrichtigungen
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Lokale Einstellung (nur dieses Gerät)
            </Typography>
          </Box>
          <List disablePadding>
            <ListItem
              secondaryAction={
                <Switch
                  edge="end"
                  checked={localSettings.soundOnInstantOrder}
                  onChange={handleLocalToggle('soundOnInstantOrder')}
                  color="primary"
                />
              }
            >
              <ListItemIcon>
                <SoundIcon />
              </ListItemIcon>
              <ListItemText
                primary="Ton bei Sofortbestellung"
                secondary="Akustisches Signal bei neuen Sofortbestellungen"
              />
            </ListItem>
            <Divider component="li" />
            <ListItem
              secondaryAction={
                <Switch
                  edge="end"
                  checked={localSettings.soundOnPreorder}
                  onChange={handleLocalToggle('soundOnPreorder')}
                  color="primary"
                />
              }
            >
              <ListItemIcon>
                <ScheduleIcon />
              </ListItemIcon>
              <ListItemText
                primary="Ton bei Vorbestellung"
                secondary="Akustisches Signal bei neuen Vorbestellungen"
              />
            </ListItem>
          </List>
        </SettingsGroup>

        <Box sx={!canManageGlobalSettings ? { opacity: 0.62, pointerEvents: 'none' } : undefined}>
          <SettingsGroup title="Betrieb & Disposition">

        {/* Abschnitt: Vorbestellungen */}
        <Box sx={{ p: 2, bgcolor: 'grey.100' }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Vorbestellungen
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Globale Einstellung (gilt für alle Fahrer)
          </Typography>
        </Box>
        <List disablePadding>
          <ListItem
            secondaryAction={
              <Switch
                edge="end"
                checked={globalSettings.showPreordersToAllDrivers || false}
                onChange={handleImmediateToggle('showPreordersToAllDrivers', globalSettings.showPreordersToAllDrivers || false)}
                color="primary"
              />
            }
          >
            <ListItemIcon>
              <VisibilityIcon />
            </ListItemIcon>
            <ListItemText
              primary="Vorbestellungen allen Fahrern anzeigen"
              secondary="Alle Fahrer können Vorbestellungen sehen und annehmen"
            />
          </ListItem>
          <ListItem
            secondaryAction={
              <Switch
                edge="end"
                checked={globalSettings.autoAcceptPreBookings || false}
                onChange={handleImmediateToggle('autoAcceptPreBookings', globalSettings.autoAcceptPreBookings || false)}
                color="primary"
              />
            }
          >
            <ListItemIcon>
              <ScheduleIcon />
            </ListItemIcon>
            <ListItemText
              primary="Vorbestellungen automatisch bestätigen"
              secondary="Kunden erhalten direkt eine Bestätigung. Die Fahrt bleibt bis zur Fahrerannahme als offene Vorbestellung sichtbar."
            />
          </ListItem>
        </List>
        <Box sx={{ px: 3, pb: 2 }}>
          <Alert severity="info">
            Die automatische Freigabe von Vorbestellungen wird dynamisch über das Betriebsgebiet berechnet.
            Lokale Abholungen werden 15 Minuten vor Abholzeit freigegeben; externe Abholungen früher anhand der Google-Maps-Fahrzeit.
          </Alert>
        </Box>

        <Divider />

        {/* Abschnitt: Navigation / Routenberechnung */}
        <Box sx={{ p: 2, bgcolor: 'grey.100' }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Navigation &amp; Routenberechnung
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Globaler Standard für die Fahrer-Navigation. Jeder Fahrer kann ihn in seinen App-Einstellungen überschreiben.
          </Typography>
        </Box>
        <List disablePadding>
          <ListItem
            secondaryAction={
              <Switch
                edge="end"
                checked={globalSettings.routeShortest || false}
                onChange={handleImmediateToggle('routeShortest', globalSettings.routeShortest || false)}
                color="primary"
              />
            }
          >
            <ListItemIcon>
              <AltRouteIcon />
            </ListItemIcon>
            <ListItemText
              primary="Kürzeste Strecke bevorzugen"
              secondary="Statt der schnellsten Route wird die distanzkürzeste berechnet (kann kleinere Straßen bevorzugen)."
            />
          </ListItem>
          <ListItem
            secondaryAction={
              <Switch
                edge="end"
                checked={globalSettings.routeAvoidHighways || false}
                onChange={handleImmediateToggle('routeAvoidHighways', globalSettings.routeAvoidHighways || false)}
                color="primary"
              />
            }
          >
            <ListItemIcon>
              <AvoidIcon />
            </ListItemIcon>
            <ListItemText
              primary="Autobahnen meiden"
              secondary="Routen vermeiden nach Möglichkeit Autobahnen."
            />
          </ListItem>
          <ListItem
            secondaryAction={
              <Switch
                edge="end"
                checked={globalSettings.routeAvoidTolls || false}
                onChange={handleImmediateToggle('routeAvoidTolls', globalSettings.routeAvoidTolls || false)}
                color="primary"
              />
            }
          >
            <ListItemIcon>
              <AvoidIcon />
            </ListItemIcon>
            <ListItemText
              primary="Mautstraßen meiden"
              secondary="Routen vermeiden nach Möglichkeit kostenpflichtige Straßen."
            />
          </ListItem>
          <ListItem
            secondaryAction={
              <Switch
                edge="end"
                checked={globalSettings.routeAvoidFerries || false}
                onChange={handleImmediateToggle('routeAvoidFerries', globalSettings.routeAvoidFerries || false)}
                color="primary"
              />
            }
          >
            <ListItemIcon>
              <AvoidIcon />
            </ListItemIcon>
            <ListItemText
              primary="Fähren meiden"
              secondary="Routen vermeiden nach Möglichkeit Fährverbindungen."
            />
          </ListItem>
          <ListItem
            secondaryAction={
              <Switch
                edge="end"
                checked={globalSettings.trafficOptimal || false}
                onChange={handleImmediateToggle('trafficOptimal', globalSettings.trafficOptimal || false)}
                color="primary"
              />
            }
          >
            <ListItemIcon>
              <TrafficIcon />
            </ListItemIcon>
            <ListItemText
              primary="Höchste ETA-Genauigkeit"
              secondary="Genauere, verkehrsabhängige Ankunftszeiten (etwas langsamere Berechnung)."
            />
          </ListItem>
        </List>

        <Divider />

        {/* Abschnitt: Betriebsgebiet */}
        <Box sx={{ p: 2, bgcolor: 'grey.100' }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Betriebsgebiet
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Steuert die automatische Freigabe von Vorbestellungen
          </Typography>
        </Box>
        <List disablePadding>
          <ListItem
            secondaryAction={
              <Switch
                edge="end"
                checked={globalSettings.serviceAreaEnabled === true}
                onChange={handleServiceAreaToggle}
                color="primary"
              />
            }
          >
            <ListItemIcon>
              <MapIcon />
            </ListItemIcon>
            <ListItemText
              primary="Betriebsgebiet aktivieren"
              secondary="Bei aktiver Einstellung werden externe Abholungen früher anhand der Google-Maps-Fahrzeit freigegeben"
            />
          </ListItem>
        </List>
        <Box sx={{ px: 3, py: 2 }}>
          <Alert severity={globalSettings.serviceAreaEnabled === true ? 'info' : 'warning'} sx={{ mb: 2 }}>
            {globalSettings.serviceAreaEnabled === true
              ? `Lokale Vorbestellungen werden fest 15 Minuten vor Abholzeit freigegeben. Außerhalb des Betriebsgebiets nutzt NextCab die Google-Maps-Fahrzeit plus ${SERVICE_AREA_OUTSIDE_BUFFER_MINUTES} Minuten Puffer; falls keine Fahrzeit ermittelt werden kann, werden ${SERVICE_AREA_OUTSIDE_FALLBACK_MINUTES} Minuten verwendet. Die automatische Freigabe erfolgt maximal ${SERVICE_AREA_MAX_LEAD_MINUTES} Minuten vor Abholzeit. Derselbe Zeitpunkt wird auch für die Online-Prüfung bereits zugewiesener Vorbestellungen verwendet.`
              : 'Betriebsgebiet ist deaktiviert. Vorbestellungen nutzen weiterhin die feste lokale Standardfreigabe von 15 Minuten.'}
          </Alert>

          {globalSettings.serviceAreaEnabled === true && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <FormLabel component="legend" sx={{ mb: 1, fontWeight: 'bold', fontSize: '0.875rem' }}>
                    Betriebsgebiet-Modus
                  </FormLabel>
                  <RadioGroup
                    value={serviceAreaMode}
                    onChange={handleImmediateValueSetting('serviceAreaMode')}
                  >
                    <FormControlLabel
                      value="radius"
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight="bold">Radius-Fallback</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Nutzt automatisch den Hauptsitz aus dem Impressum plus festen Radius.
                          </Typography>
                        </Box>
                      }
                      sx={{ alignItems: 'flex-start', mb: 1 }}
                    />
                    <FormControlLabel
                      value="polygon"
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight="bold">Festes Gebiet (Polygon)</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Nutzt das in der Kartenverwaltung hinterlegte Betriebsgebiet.
                          </Typography>
                        </Box>
                      }
                      sx={{ alignItems: 'flex-start', mb: 1 }}
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>

              {serviceAreaMode === 'radius' && (
                <>
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                  <Grid item xs={12}>
                    <Alert severity="info">
                      Hauptsitz aus dem Impressum: <strong>{globalSettings.serviceAreaFallbackAddress || brandingConfig.address}</strong>
                      {globalSettings.serviceAreaFallbackLat != null && globalSettings.serviceAreaFallbackLng != null && (
                        <> ({Number(globalSettings.serviceAreaFallbackLat).toFixed(6)}, {Number(globalSettings.serviceAreaFallbackLng).toFixed(6)})</>
                      )}
                      <br />
                      Radius-Fallback: <strong>{SERVICE_AREA_RADIUS_METERS / 1000} km</strong> um den Hauptsitz. Diese Vorgabe ist fest hinterlegt.
                    </Alert>
                  </Grid>
                </>
              )}

              {serviceAreaMode === 'polygon' && (
                <Grid item xs={12}>
                  <Stack spacing={2}>
                    <Alert severity={hasServiceAreaPolygon ? 'success' : 'info'}>
                      {hasServiceAreaPolygon
                        ? 'Ein festes Betriebsgebiet ist hinterlegt und kann in der Kartenverwaltung bearbeitet werden.'
                        : 'Legen Sie das Betriebsgebiet als festes Gebiet in der Kartenverwaltung fest.'}
                    </Alert>
                    <Button
                      variant="contained"
                      startIcon={<MapIcon />}
                      onClick={() => navigate('/einstellungen/sektoren')}
                      sx={{ alignSelf: 'flex-start' }}
                    >
                      {hasServiceAreaPolygon ? 'Betriebsgebiet verwalten' : 'Betriebsgebiet festlegen'}
                    </Button>
                  </Stack>
                </Grid>
              )}
            </Grid>
          )}
        </Box>

        <Divider />

        {/* Abschnitt: Auftragsdisposition */}
        <Box sx={{ p: 2, bgcolor: 'grey.100' }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Auftragsdisposition
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Globale Einstellung (Anzeige-Methode für Sofortbestellungen &amp; fällige Vorbestellungen)
          </Typography>
        </Box>
        <Box sx={{ px: 3, py: 2 }}>
          <FormControl component="fieldset">
            <FormLabel component="legend" sx={{ mb: 1, fontWeight: 'bold', fontSize: '0.875rem' }}>
              Wie sollen neue Aufträge (Sofort- &amp; fällige Vorbestellungen) den Fahrern angezeigt werden?
            </FormLabel>
            <RadioGroup
              value={sofortbestellungAnzeigeMethode}
              onChange={(e) => handleDispatchMethodChangeRequest(e.target.value)}
            >
              <FormControlLabel
                value="distance"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight="bold">Entfernungsbasiert (Standard)</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Der nächste passende Fahrer erhält die Fahrt exklusiv nach Entfernung/Fahrzeit
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', mb: 1 }}
              />
              <Box sx={{ ml: 4, mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={7}>
                    <Typography variant="body2" fontWeight="bold">
                      Angebotsdauer für entfernungsbasierte Angebote
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Fahrer werden einzeln gefragt. Nach Ablauf wird automatisch der nächste passende Fahrer angefragt.
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Angebotsdauer"
                      value={globalSettings.distanceExclusiveOfferSeconds ?? 45}
                      onChange={handleImmediateNumberSetting('distanceExclusiveOfferSeconds', 10, 120)}
                      InputProps={{ endAdornment: <InputAdornment position="end">Sek.</InputAdornment> }}
                      helperText="Mind. 10 Sek., max. 120 Sek."
                    />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                <List disablePadding>
                  <ListItem
                    disableGutters
                    secondaryAction={
                      <Switch
                        edge="end"
                        checked={globalSettings.distanceAutoFollowUpEnabled === true}
                        onChange={handleImmediateToggle(
                          'distanceAutoFollowUpEnabled',
                          globalSettings.distanceAutoFollowUpEnabled === true
                        )}
                        color="primary"
                      />
                    }
                  >
                    <ListItemIcon>
                      <AutoFollowUpIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Automatische Anschlusstouren anbieten"
                      secondary="Fahrer mit Fahrgast, deren Fahrtziel nahe am neuen Abholort liegt und die dort bald frei werden, werden gleichberechtigt mit freien Fahrern nach erwarteter Ankunft berücksichtigt. Der Fahrer muss das Angebot aktiv als Anschlusstour annehmen."
                    />
                  </ListItem>
                </List>
                <Alert severity="info" sx={{ mt: 2 }}>
                  Verfügbare und besetzte Fahrer werden gemeinsam nach erwarteter Ankunft
                  (ETA) gereiht. Bei nahezu gleicher ETA gewinnt der freie Fahrer, da seine
                  Ankunft sicherer ist. Ein Fahrer mit Fahrgast kommt in Frage, wenn sein
                  Fahrtziel maximal 700 m vom neuen Abholort entfernt liegt – er also praktisch
                  ohnehin dorthin fährt. Er gewinnt nur, wenn er laut ETA früher (mit
                  Sicherheitsabstand) am Abholort ist als jeder freie Fahrer; so wird ein
                  besetztes Taxi, das genau zum Abholort fährt, nicht durch eine lange
                  Leerfahrt eines freien Fahrers verdrängt.
                </Alert>
              </Box>
              <FormControlLabel
                value="sectors"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight="bold">Nach Sektoren</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Fahrer im Abholsektor werden nach Sektor-Warteschlange exklusiv angeboten, danach greift der Entfernungs-Fallback
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', mt: 1, mb: 1 }}
              />
              <Box sx={{ ml: 4, mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
                <Box>
                  <Typography variant="body2" fontWeight="bold">
                    {activeSectorCount} aktive Sektoren
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Sektoren als präzise Polygone vorbereiten, zeichnen, bearbeiten und verwalten.
                  </Typography>
                </Box>
                <Button
                  variant={isSectorDispatchActive ? 'contained' : 'outlined'}
                  startIcon={<MapIcon />}
                  onClick={() => navigate('/einstellungen/sektoren')}
                >
                  Sektoren verwalten
                </Button>
              </Stack>

              {!isSectorDispatchActive && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Die Sektoren können vorbereitet werden, ohne die aktuelle entfernungsbasierte Disposition zu verändern.
                  Wirksam werden sie erst nach dem Umstellen auf „Nach Sektoren“.
                </Alert>
              )}

              {activeSectorCount === 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Es sind noch keine aktiven Sektoren angelegt. Die spätere Sektor-Disposition fällt dann auf Entfernung zurück.
                </Alert>
              )}

              {isSectorDispatchActive && (
                <>
                  <Divider sx={{ my: 2 }} />

                  <Grid container spacing={2} alignItems="center" sx={{ mb: 1 }}>
                    <Grid item xs={12} md={7}>
                      <Typography variant="body2" fontWeight="bold">
                        Angebotsdauer für Sektor-Angebote
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Gilt einheitlich für Sektor-Warteschlange, Zielsektor-Reservierungen,
                        ankommende Fahrer mit Fahrgast und Distanz-Fallback. Nach Ablauf wird
                        automatisch der nächste passende Fahrer angefragt.
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Angebotsdauer"
                        value={globalSettings.sectorExclusiveOfferSeconds ?? 45}
                        onChange={handleImmediateNumberSetting('sectorExclusiveOfferSeconds', 10, 120)}
                        InputProps={{ endAdornment: <InputAdornment position="end">Sek.</InputAdornment> }}
                        helperText="Mind. 10 Sek., max. 120 Sek."
                      />
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 2 }} />

                  <List disablePadding>
                    <ListItem
                      disableGutters
                      secondaryAction={
                        <Switch
                          edge="end"
                          checked={globalSettings.sectorAutoFollowUpEnabled === true}
                          onChange={handleImmediateToggle(
                            'sectorAutoFollowUpEnabled',
                            globalSettings.sectorAutoFollowUpEnabled === true
                          )}
                          color="primary"
                        />
                      }
                    >
                      <ListItemIcon>
                        <AutoFollowUpIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Automatische Anschlusstouren anbieten"
                        secondary="Wenn im Abholsektor kein freies Taxi verfügbar ist, können Fahrer mit Fahrgast berücksichtigt werden, die dort voraussichtlich bald frei werden. Der Fahrer muss das Angebot aktiv als Anschlusstour annehmen."
                      />
                    </ListItem>
                  </List>

                  <Box sx={{ mt: 2 }}>
                    <TextField
                      select
                      fullWidth
                      label="Priorität außerhalb des Abholsektors"
                      value={globalSettings.sectorFallbackPriorityMode ?? 'target_sector_first'}
                      onChange={handleImmediateValueSetting('sectorFallbackPriorityMode')}
                      helperText="Freie Fahrer im Abholsektor stehen unabhängig davon immer zuerst."
                    >
                      <MenuItem value="target_sector_first">
                        Zielsektor bevorzugen (Standard)
                      </MenuItem>
                      <MenuItem value="fastest_eta">
                        Schnellste erwartete Ankunft
                      </MenuItem>
                    </TextField>
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Feste Regeln: Zielsektor-Reservierungen gelten 15 Minuten. Fahrer mit
                      Fahrgast kommen in Frage, wenn ihr Fahrtziel im Abholsektor und maximal
                      700 m vom neuen Abholort liegt – sie also praktisch dorthin fahren. Sie
                      gewinnen nur, wenn sie laut ETA früher (mit Sicherheitsabstand) am
                      Abholort sind als ein freier Fahrer.
                    </Alert>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                    <AltRouteIcon color="primary" sx={{ mr: 1.5, mt: 0.25 }} />
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        Zielsektor-Reservierung
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Fahrer können in der App jederzeit einen Zielsektor wählen, fahren besetzt
                        dorthin und halten bis zur Ankunft eine reservierte Position. Ist der
                        Zielsektor leer, erhalten sie dort bereits unterwegs Anschlusstouren.
                        Immer aktiv (nur bei „Nach Sektoren" sichtbar).
                      </Typography>
                    </Box>
                  </Box>

                  <Alert severity="info" sx={{ mb: 2 }}>
                    Eine Zielsektor-Reservierung gilt fest 15 Minuten und wird nach freien
                    Fahrern im Sektor gemäß der oben gewählten Priorität berücksichtigt.
                  </Alert>

                  <Divider sx={{ my: 2 }} />

                  <List disablePadding>
                    <ListItem
                      disableGutters
                      secondaryAction={
                        <Switch
                          edge="end"
                          checked={globalSettings.sectorShowDriverAddress !== false}
                          onChange={handleImmediateToggle(
                            'sectorShowDriverAddress',
                            globalSettings.sectorShowDriverAddress !== false
                          )}
                          color="primary"
                        />
                      }
                    >
                      <ListItemIcon>
                        <VisibilityIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Genaue Fahrerstandorte anzeigen"
                        secondary="Zeigt in der Sektor-Fahrzeugliste der Fahrer-App Straße und Ort der Kollegen. Fahrzeugnummer und Anmeldezeit bleiben unabhängig davon immer sichtbar."
                      />
                    </ListItem>
                  </List>
                </>
              )}
              </Box>
            </RadioGroup>
          </FormControl>
        </Box>

        <Divider />

        {/* Automatische Abmeldung (global, Cloud Function).
            Fahrer mit beendeter App (kein präzises Tracking mehr) erhalten
            IMMER 3 Push-Warnungen (1/3–3/3) — das ist bewusst nicht
            abschaltbar. Der Switch steuert nur, ob nach der 3. Warnung
            server-seitig abgemeldet wird.
            Konzept: nextcab_mobile/docs/general/app_kill_nudge_logout_konzept.md */}
        <Box sx={{ p: 2, bgcolor: 'grey.100' }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Automatische Abmeldung
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Globale Einstellung (Cloud Function – gilt für alle Fahrer)
          </Typography>
        </Box>
        <List disablePadding>
          <ListItem
            secondaryAction={
              <Switch
                edge="end"
                checked={cloudFunctionSettings.dripLogoutAfterWarningsEnabled ?? false}
                onChange={async () => {
                  const newValue = !(cloudFunctionSettings.dripLogoutAfterWarningsEnabled ?? false);
                  try {
                    await dispatch(saveCloudFunctionSetting({
                      key: 'dripLogoutAfterWarningsEnabled',
                      value: newValue
                    })).unwrap();
                    setSnackbar({ open: true, message: 'Einstellung gespeichert', severity: 'success' });
                  } catch (error) {
                    setSnackbar({ open: true, message: `Fehler: ${error}`, severity: 'error' });
                  }
                }}
                color="primary"
              />
            }
          >
            <ListItemIcon>
              <AutoLogoutIcon />
            </ListItemIcon>
            <ListItemText
              primary="Nach der 3. Warnung automatisch abmelden"
              secondary="Fahrer mit beendeter App erhalten immer 3 Push-Warnungen (1/3–3/3). Ist diese Option aktiv, werden sie danach server-seitig abgemeldet (Schicht wird beendet, Fahrzeug freigegeben) – sonst bleibt es bei den Warnungen."
            />
          </ListItem>
        </List>

        </SettingsGroup>

        <SettingsGroup title="Preise & Abrechnung">

        {/* Abschnitt: Tarif */}
        <Box sx={{ p: 2, bgcolor: 'grey.100' }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Tarif
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Globale Einstellung (Fahrpreisberechnung für alle Fahrten)
          </Typography>
        </Box>
        <Box sx={{ px: 3, py: 2, bgcolor: 'grey.50' }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            <strong>Formel:</strong> Grundpreis + Distanz &times; Preis/km (+ Großraumzuschlag bei Kombi/Spezialfahrzeug)
            <br />
            <strong>Standard 5 km:</strong> {calculateFarePreview(5)} &euro; &nbsp;|&nbsp;
            <strong>10 km:</strong> {calculateFarePreview(10)} &euro;
            <br />
            <strong>Großraum 5 km:</strong> {calculateFarePreview(5, true)} &euro; &nbsp;|&nbsp;
            <strong>10 km:</strong> {calculateFarePreview(10, true)} &euro;
            {globalSettings.nachtTarifAktiv === true && (
              <>
                <br />
                <br />
                <strong>Nachttarif 5 km:</strong> {calculateNightFarePreview(5)} &euro; &nbsp;|&nbsp;
                <strong>10 km:</strong> {calculateNightFarePreview(10)} &euro;
                <br />
                <strong>Nachttarif Großraum 5 km:</strong> {calculateNightFarePreview(5, true)} &euro; &nbsp;|&nbsp;
                <strong>10 km:</strong> {calculateNightFarePreview(10, true)} &euro;
              </>
            )}
          </Alert>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Grundpreis (Flaggenfall)"
                type="number"
                value={formState.tarifGrundpreis}
                onChange={handleDecimalChange('tarifGrundpreis', 0, 50)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EuroIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">&euro;</InputAdornment>
                  ),
                  inputProps: { min: 0, max: 50, step: 0.1 }
                }}
                helperText="Startpreis beim Einsteigen (0-50 &euro;)"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Preis pro Kilometer"
                type="number"
                value={formState.tarifPreisProKm}
                onChange={handleDecimalChange('tarifPreisProKm', 0.1, 20)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <RidesIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">&euro;/km</InputAdornment>
                  ),
                  inputProps: { min: 0.1, max: 20, step: 0.1 }
                }}
                helperText="Kilometerpreis (0,10-20 &euro;)"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Großraumzuschlag"
                type="number"
                value={formState.tarifGrossraumzuschlag}
                onChange={handleDecimalChange('tarifGrossraumzuschlag', 0, 100)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EuroIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">&euro;</InputAdornment>
                  ),
                  inputProps: { min: 0, max: 100, step: 0.5 }
                }}
                helperText="Zuschlag für Kombi und Spezialfahrzeug (0-100 &euro;)"
              />
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, mb: 1 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              onClick={handleSaveTarif}
              disabled={isSaving}
            >
              {isSaving ? 'Wird gespeichert...' : 'Tarif speichern'}
            </Button>
          </Box>
        </Box>

        {/* Abschnitt: Nachttarif */}
        <Box sx={{ p: 2, bgcolor: 'grey.100' }}>
          <Typography variant="subtitle1" fontWeight="bold">
            <NightIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            Nachttarif
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Separate Preise für Nachtfahrten (Mo-Fr und Sa-So konfigurierbar)
          </Typography>
        </Box>
        <List disablePadding>
          <ListItem
            secondaryAction={
              <Switch
                edge="end"
                checked={globalSettings.nachtTarifAktiv === true}
                onChange={handleImmediateToggle('nachtTarifAktiv', globalSettings.nachtTarifAktiv === true)}
                color="primary"
              />
            }
          >
            <ListItemIcon>
              <NightIcon />
            </ListItemIcon>
            <ListItemText
              primary="Nachttarif aktivieren"
              secondary="Separate Preise außerhalb der regulären Geschäftszeiten"
            />
          </ListItem>
        </List>

        <Collapse in={globalSettings.nachtTarifAktiv === true} timeout="auto" unmountOnExit>
          <Box sx={{ px: 3, py: 2, bgcolor: 'grey.50' }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              <strong>Vergleich (5 km):</strong> Tag {calculateFarePreview(5)} &euro; &rarr; Nacht {calculateNightFarePreview(5)} &euro;
              &nbsp;|&nbsp;
              <strong>10 km:</strong> Tag {calculateFarePreview(10)} &euro; &rarr; Nacht {calculateNightFarePreview(10)} &euro;
              <br />
              <strong>Großraum (5 km):</strong> Tag {calculateFarePreview(5, true)} &euro; &rarr; Nacht {calculateNightFarePreview(5, true)} &euro;
              &nbsp;|&nbsp;
              <strong>10 km:</strong> Tag {calculateFarePreview(10, true)} &euro; &rarr; Nacht {calculateNightFarePreview(10, true)} &euro;
              <br /><br />
              <strong>Großraumzuschlag</strong> gilt Tag und Nacht gleich.
            </Alert>

            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
              Nacht-Preise
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nacht-Grundpreis (Flaggenfall)"
                  type="number"
                  value={formState.nachtTarifGrundpreis}
                  onChange={handleDecimalChange('nachtTarifGrundpreis', 0, 50)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EuroIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">&euro;</InputAdornment>
                    ),
                    inputProps: { min: 0, max: 50, step: 0.1 }
                  }}
                  helperText="Startpreis nachts beim Einsteigen"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nacht-Preis pro Kilometer"
                  type="number"
                  value={formState.nachtTarifPreisProKm}
                  onChange={handleDecimalChange('nachtTarifPreisProKm', 0.1, 20)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <RidesIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">&euro;/km</InputAdornment>
                    ),
                    inputProps: { min: 0.1, max: 20, step: 0.1 }
                  }}
                  helperText="Kilometerpreis nachts"
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
              Zeitfenster: Wochentage (Mo–Fr)
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nachttarif beginnt (Mo-Fr)"
                  type="time"
                  value={formState.nachtTarifWocheStart}
                  onChange={handleChange('nachtTarifWocheStart')}
                  InputLabelProps={{ shrink: true }}
                  helperText="z.B. 22:00"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nachttarif endet (Mo-Fr)"
                  type="time"
                  value={formState.nachtTarifWocheEnd}
                  onChange={handleChange('nachtTarifWocheEnd')}
                  InputLabelProps={{ shrink: true }}
                  helperText="z.B. 06:00 (nächster Tag)"
                />
              </Grid>
            </Grid>

            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 3, mb: 2 }}>
              Zeitfenster: Wochenende (Sa–So)
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nachttarif beginnt (Sa-So)"
                  type="time"
                  value={formState.nachtTarifWochenendStart}
                  onChange={handleChange('nachtTarifWochenendStart')}
                  InputLabelProps={{ shrink: true }}
                  helperText="z.B. 20:00"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nachttarif endet (Sa-So)"
                  type="time"
                  value={formState.nachtTarifWochenendEnd}
                  onChange={handleChange('nachtTarifWochenendEnd')}
                  InputLabelProps={{ shrink: true }}
                  helperText="z.B. 07:00 (nächster Tag)"
                />
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, mb: 1 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                onClick={handleSaveNachtTarif}
                disabled={isSaving}
              >
                {isSaving ? 'Wird gespeichert...' : 'Nachttarif speichern'}
              </Button>
            </Box>
          </Box>
        </Collapse>

        {/* Abschnitt: Anfahrtskosten (außerhalb Betriebsgebiet) */}
        <Box sx={{ p: 2, bgcolor: 'grey.100' }}>
          <Typography variant="subtitle1" fontWeight="bold">
            <AnfahrtIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            Anfahrtskosten
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Gebühr pro km ab Betriebsgebietsgrenze, wenn Abholung und Ziel außerhalb liegen
          </Typography>
        </Box>
        <List disablePadding>
          <ListItem
            secondaryAction={
              <Switch
                edge="end"
                checked={globalSettings.anfahrtEnabled === true}
                onChange={handleImmediateToggle('anfahrtEnabled', globalSettings.anfahrtEnabled === true)}
                color="primary"
              />
            }
          >
            <ListItemIcon>
              <AnfahrtIcon />
            </ListItemIcon>
            <ListItemText
              primary="Anfahrtskosten aktivieren"
              secondary="Gilt nur für Fahrten von außerhalb nach außerhalb; bei Festpreisen ist die Anfahrt bereits einkalkuliert"
            />
          </ListItem>
        </List>

        <Collapse in={globalSettings.anfahrtEnabled === true} timeout="auto" unmountOnExit>
          <Box sx={{ px: 3, py: 2, bgcolor: 'grey.50' }}>
            {globalSettings.serviceAreaEnabled !== true && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Anfahrtskosten benötigen ein konfiguriertes <strong>Betriebsgebiet</strong> (Seite
                „Sektoren"). Solange keines aktiv ist, wird keine Gebühr berechnet.
              </Alert>
            )}
            <Alert severity="info" sx={{ mb: 3 }}>
              <strong>Berechnung:</strong> Routen-km von der Betriebsgebietsgrenze bis zur Abholadresse &times; Preis/km
              — nur wenn Abhol- <strong>und</strong> Zieladresse außerhalb des Betriebsgebiets liegen.
              <br />
              <strong>Beispiel 5 km:</strong> {(Math.round(5 * formState.anfahrtPreisProKm * 10) / 10).toFixed(2)} &euro; &nbsp;|&nbsp;
              <strong>10 km:</strong> {(Math.round(10 * formState.anfahrtPreisProKm * 10) / 10).toFixed(2)} &euro;
              <br />
              Der Betrag wird dem Fahrer als Notiz und Hinweis angezeigt und ist in der
              Fahrpreis-Schätzung des Kunden enthalten („davon Anfahrt …").
            </Alert>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Anfahrtspreis pro Kilometer"
                  type="number"
                  value={formState.anfahrtPreisProKm}
                  onChange={handleDecimalChange('anfahrtPreisProKm', 0.05, formState.tarifPreisProKm)}
                  error={formState.anfahrtPreisProKm > formState.tarifPreisProKm}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EuroIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">&euro;/km</InputAdornment>
                    ),
                    inputProps: { min: 0.05, max: formState.tarifPreisProKm, step: 0.05 }
                  }}
                  helperText={`Maximal ${formState.tarifPreisProKm.toFixed(2).replace('.', ',')} €/km (Kilometerpreis des Tarifs)`}
                />
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, mb: 1 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                onClick={handleSaveAnfahrt}
                disabled={isSaving}
              >
                {isSaving ? 'Wird gespeichert...' : 'Anfahrtskosten speichern'}
              </Button>
            </Box>
          </Box>
        </Collapse>

        {/* Abschnitt: Festpreise */}
        <Box sx={{ p: 2, bgcolor: 'grey.100' }}>
          <Typography variant="subtitle1" fontWeight="bold">
            <ListAltIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            Festpreisliste
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Vordefinierte Festpreise für Strecken, die Kunden in der App zur Auswahl angezeigt werden
          </Typography>
        </Box>
        <Box sx={{ px: 3, py: 2, bgcolor: 'grey.50' }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>So funktioniert das Festpreis-System:</strong>
              <Box component="ul" sx={{ m: 0, pl: 2, mt: 0.5 }}>
                <li>Aktive Einträge werden dem Kunden in der App als auswählbare Strecken angezeigt.</li>
                <li>Einträge sind <strong>richtungsabhängig</strong> — für Hin- und Rückfahrt separate Einträge anlegen.</li>
                <li>Der <strong>Name</strong> (z.B. „Flughafen") wird dem Kunden als Chip angezeigt, die <strong>Adresse</strong> dient dem PLZ-Abgleich: Die App prüft, ob das vom Kunden eingegebene Ziel zur hinterlegten Adresse passt.</li>
                <li><strong>Adresse fixieren:</strong> Pro Eintrag kann entweder die <strong>Startadresse</strong> oder die <strong>Zieladresse</strong> fixiert werden. Das fixierte Feld wird in der Kunden-App automatisch vorausgefüllt und ausgegraut — der Kunde kann nur das andere Feld frei eingeben.</li>
                <li>Der <strong>Großraumzuschlag</strong> (Kombi/Spezialfahrzeug) wird automatisch auf den Festpreis addiert.</li>
                <li>Der <strong>Nachttarif</strong> hat keinen Einfluss auf Festpreise.</li>
                <li>Werden alle Einträge deaktiviert oder gelöscht, verschwindet die Festpreis-Auswahl automatisch aus der Kunden-App.</li>
              </Box>
            </Alert>

            {festpreiseState.items.slice(0, 2).map((fp) => (
              <Card key={fp.id} variant="outlined" sx={{ mb: 1 }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', py: 1, '&:last-child': { pb: 1 } }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight="bold">
                      {fp.start} → {fp.ziel}: {Number(fp.preis).toFixed(2)} €
                    </Typography>
                    {(fp.startAdresse || fp.zielAdresse) && (
                      <Typography variant="caption" color="text.secondary">
                        {fp.startAdresse && `Von: ${fp.startAdresse}`}
                        {fp.startAdresse && fp.zielAdresse && ' | '}
                        {fp.zielAdresse && `Nach: ${fp.zielAdresse}`}
                      </Typography>
                    )}
                  </Box>
                  <Chip
                    label={fp.aktiv ? 'Aktiv' : 'Inaktiv'}
                    color={fp.aktiv ? 'success' : 'default'}
                    size="small"
                    sx={{ mr: 1 }}
                  />
                  <IconButton size="small" onClick={() => handleOpenFestpreisDialog(fp)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDeleteFestpreis(fp.id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </CardContent>
              </Card>
            ))}

            {festpreiseState.items.length > 2 && (
              <>
                <Collapse in={festpreiseExpanded}>
                  {festpreiseState.items.slice(2).map((fp) => (
                    <Card key={fp.id} variant="outlined" sx={{ mb: 1 }}>
                      <CardContent sx={{ display: 'flex', alignItems: 'center', py: 1, '&:last-child': { pb: 1 } }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" fontWeight="bold">
                            {fp.start} → {fp.ziel}: {Number(fp.preis).toFixed(2)} €
                          </Typography>
                          {(fp.startAdresse || fp.zielAdresse) && (
                            <Typography variant="caption" color="text.secondary">
                              {fp.startAdresse && `Von: ${fp.startAdresse}`}
                              {fp.startAdresse && fp.zielAdresse && ' | '}
                              {fp.zielAdresse && `Nach: ${fp.zielAdresse}`}
                            </Typography>
                          )}
                        </Box>
                        <Chip
                          label={fp.aktiv ? 'Aktiv' : 'Inaktiv'}
                          color={fp.aktiv ? 'success' : 'default'}
                          size="small"
                          sx={{ mr: 1 }}
                        />
                        <IconButton size="small" onClick={() => handleOpenFestpreisDialog(fp)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDeleteFestpreis(fp.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </CardContent>
                    </Card>
                  ))}
                </Collapse>
                <Button
                  size="small"
                  onClick={() => setFestpreiseExpanded(!festpreiseExpanded)}
                  startIcon={festpreiseExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  sx={{ mb: 1 }}
                >
                  {festpreiseExpanded
                    ? 'Weniger anzeigen'
                    : `${festpreiseState.items.length - 2} weitere anzeigen`}
                </Button>
              </>
            )}

            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => handleOpenFestpreisDialog()}
              sx={{ mt: 1 }}
            >
              Festpreis hinzufügen
            </Button>
        </Box>

        <Divider />

        {/* Zahlung per App (PayPal) — Verwaltung nur durch die Hauptzentrale;
            Sub-Konten sehen die Sektion ausgegraut */}
        <AppPaymentSettingsSection
          onEnabledChange={handleAppPaymentsEnabledChange}
          canManage={canManageGlobalSettings}
        />

        <Divider />

        <List disablePadding>
          <ListItem
            secondaryAction={
              <Switch
                edge="end"
                checked={appPaymentsEnabled ? true : (globalSettings.showFareToCustomer || false)}
                onChange={handleImmediateToggle('showFareToCustomer', globalSettings.showFareToCustomer || false)}
                disabled={appPaymentsEnabled}
                color="primary"
              />
            }
          >
            <ListItemIcon>
              <EuroIcon />
            </ListItemIcon>
            <ListItemText
              primary="Fahrpreis-Schätzung für Kunden anzeigen"
              secondary={appPaymentsEnabled ? lockedByAppPayments : 'Kunden sehen den geschätzten Fahrpreis beim Bestellen'}
            />
          </ListItem>
        </List>

        <Divider />

        {/* Abschnitt: Fahrerabrechnung */}
        <Box sx={{ p: 2, bgcolor: 'grey.100' }}>
          <Typography variant="subtitle1" fontWeight="bold">
            <EuroIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            Fahrerabrechnung
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Steuert echte Fahrpreis-Eingaben in der Fahrer-App
          </Typography>
        </Box>
        <List disablePadding>
          <ListItem
            secondaryAction={
              <Switch
                edge="end"
                checked={fareInputLocked ? true : (globalSettings.requireDriverFareInput || false)}
                onChange={handleImmediateToggle('requireDriverFareInput', globalSettings.requireDriverFareInput || false)}
                disabled={fareInputLocked}
                color="primary"
              />
            }
          >
            <ListItemIcon>
              <TaxiIcon />
            </ListItemIcon>
            <ListItemText
              primary="Fahrpreis nach App-Fahrt erfassen"
              secondary={fareInputLockedHint ?? 'Fahrer werden beim Abschließen einer normalen Fahrt nach dem tatsächlichen Fahrpreis gefragt'}
            />
          </ListItem>
          {/* Unter-Option: Auto-Abschluss am Zielort — nur sichtbar, wenn keine
              Preiseingabe verlangt wird (sonst braucht der Abschluss den Dialog). */}
          {!fareInputActive && (
            <ListItem
              sx={{ pl: 6, py: 0.25 }}
              secondaryAction={
                <Switch
                  edge="end"
                  size="small"
                  checked={globalSettings.autoCompleteRideOnArrival || false}
                  onChange={handleImmediateToggle('autoCompleteRideOnArrival', globalSettings.autoCompleteRideOnArrival || false)}
                  color="primary"
                />
              }
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                <SubOptionIcon fontSize="small" color="disabled" />
              </ListItemIcon>
              <ListItemText
                primary="Fahrt automatisch abschließen bei Ankunft am Zielort"
                primaryTypographyProps={{ variant: 'body2' }}
                secondary="Die Fahrer-App schließt die Fahrt per Standort-Erkennung automatisch ab, wenn das Fahrzeug am Zielort ankommt — auch wenn die App im Hintergrund läuft. Gilt nicht für Fahrten mit Preis- oder Nachweis-Pflicht (z.B. Krankenfahrten)."
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
          )}
          <ListItem
            secondaryAction={
              <Switch
                edge="end"
                checked={globalSettings.requireFareForMedicalTransportRides !== false}
                onChange={handleImmediateToggle('requireFareForMedicalTransportRides', globalSettings.requireFareForMedicalTransportRides !== false)}
                color="primary"
              />
            }
          >
            <ListItemIcon>
              <MedicalIcon />
            </ListItemIcon>
            <ListItemText
              primary="Fahrpreis für Krankenfahrten erfassen"
              secondary="Krankenfahrten müssen beim Abschluss einen tatsächlichen Abrechnungsbetrag bekommen"
            />
          </ListItem>
          <ListItem
            secondaryAction={
              <Switch
                edge="end"
                checked={globalSettings.requireFareForManualDriverRides !== false}
                onChange={handleImmediateToggle('requireFareForManualDriverRides', globalSettings.requireFareForManualDriverRides !== false)}
                color="primary"
              />
            }
          >
            <ListItemIcon>
              <RidesIcon />
            </ListItemIcon>
            <ListItemText
              primary="Fahrpreis für Einsteiger-Touren erfassen"
              secondary="Laufkunden- und manuelle Fahrten sollen beim Abschluss einen Preis bekommen"
            />
          </ListItem>
        </List>
        <Box sx={{ px: 3, py: 2, bgcolor: 'grey.50' }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Minimaler Fahrpreis"
                value={globalSettings.driverFareInputMinEur ?? 0}
                onChange={handleImmediateNumberSetting('driverFareInputMinEur', 0, 999)}
                InputProps={{ endAdornment: <InputAdornment position="end">&euro;</InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Maximaler Fahrpreis"
                value={globalSettings.driverFareInputMaxEur ?? 999}
                onChange={handleImmediateNumberSetting('driverFareInputMaxEur', 1, 9999)}
                InputProps={{ endAdornment: <InputAdornment position="end">&euro;</InputAdornment> }}
              />
            </Grid>
          </Grid>
          <Alert severity="info" sx={{ mt: 2 }}>
            Einsteiger-Touren werden als echte Fahrten mit Laufkunde angelegt und sind dadurch direkt in Fahrtenverwaltung und Berichten sichtbar.
          </Alert>
        </Box>

        <Divider />

        {/* Quittungen & Rechnungen — EINE Sektion mit geteilter Basis
            (Firma/Steuer/SMTP); die Rechnungs-Untersektion steckt in
            ReceiptSettingsSection (rechnungskunden_konzept.md 4.6).
            Verwaltung nur durch die Hauptzentrale, wie App-Zahlungen */}
        <ReceiptSettingsSection
          onEnabledChange={handleReceiptsEnabledChange}
          onInvoicesEnabledChange={handleInvoicesEnabledChange}
          autoCompleteActive={globalSettings.autoCompleteRideOnArrival === true}
          canManage={canManageGlobalSettings}
        />

        </SettingsGroup>

        <SettingsGroup title="Kundenbindung">

        {/* Abschnitt: Treueprogramm (Treuekarte ODER Treuemeilen – exklusiv) */}
        <Box sx={{ p: 2, bgcolor: 'grey.100' }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Treueprogramm
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Globale Einstellung – es kann immer nur eines der beiden Systeme aktiv sein
          </Typography>
        </Box>

        {/* Modus-Auswahl */}
        <Box sx={{ px: 3, py: 2 }}>
          <FormControl>
            <FormLabel id="loyalty-mode-label">Aktives Programm</FormLabel>
            <RadioGroup
              aria-labelledby="loyalty-mode-label"
              value={formState.loyaltyProgramMode}
              onChange={handleChange('loyaltyProgramMode')}
            >
              <FormControlLabel value="off" control={<Radio />} label="Keines" />
              <FormControlLabel
                value="stamp"
                control={<Radio />}
                label="Treuekarten-System (Stempel pro Fahrt)"
              />
              <FormControlLabel
                value="miles"
                control={<Radio />}
                label="Treuemeilen-System (Meilen pro Kilometer)"
              />
            </RadioGroup>
          </FormControl>
        </Box>

        {/* ---- Treuekarten-System (System 1) ---- */}
        <Collapse in={formState.loyaltyProgramMode === 'stamp'} timeout="auto" unmountOnExit sx={{ width: '100%' }}>
        <Box sx={{ px: 3, py: 2, bgcolor: 'grey.50' }}>
          {savedProgramMode === 'stamp' ? (
            <Alert severity="success" sx={{ mb: 3 }}>
              <strong>Treuekarte aktiv:</strong> Kunden erhalten für jede {formState.loyaltyCardRidesRequired}. abgeschlossene Fahrt
              einen Rabatt von {formState.loyaltyCardDiscountEur}€ auf die nächste Fahrt.
              {globalSettings.loyaltyCardStartDate && (
                <> Aktiv seit: {new Date(globalSettings.loyaltyCardStartDate).toLocaleDateString('de-DE')}.</>
              )}
              {!formState.loyaltyCardNoEndDate && formState.loyaltyCardEndDate && (
                <> Gültig bis: {new Date(formState.loyaltyCardEndDate).toLocaleDateString('de-DE')}.</>
              )}
            </Alert>
          ) : (
            <Alert severity="info" sx={{ mb: 3 }}>
              <strong>Noch nicht gespeichert.</strong> Nach dem Speichern erhalten Kunden für jede {formState.loyaltyCardRidesRequired}. abgeschlossene Fahrt einen Rabatt von {formState.loyaltyCardDiscountEur}€.
              {savedProgramMode === 'miles' && <> Das aktuell aktive Treuemeilen-System wird dabei deaktiviert.</>}
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* Anzahl Fahrten für Rabatt */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Fahrten für Rabatt"
                type="number"
                value={formState.loyaltyCardRidesRequired}
                onChange={handleNumberChange('loyaltyCardRidesRequired', 2, 20)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <RidesIcon color="action" />
                    </InputAdornment>
                  ),
                  inputProps: { min: 2, max: 20 }
                }}
                helperText="Anzahl Fahrten bis zum Rabatt (2-20)"
              />
            </Grid>

            {/* Rabatthöhe in Euro */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Rabatthöhe"
                type="number"
                value={formState.loyaltyCardDiscountEur}
                onChange={handleNumberChange('loyaltyCardDiscountEur', 1, 50)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EuroIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">€</InputAdornment>
                  ),
                  inputProps: { min: 1, max: 50 }
                }}
                helperText="Rabatt pro Einlösung (1-50€)"
              />
            </Grid>

            {/* Enddatum */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
                Gültigkeitszeitraum
              </Typography>
            </Grid>

            {/* Checkbox für unbestimmte Zeit */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formState.loyaltyCardNoEndDate}
                    onChange={handleChange('loyaltyCardNoEndDate')}
                    color="primary"
                    icon={<CalendarIcon />}
                    checkedIcon={<InfinityIcon />}
                  />
                }
                label="Für unbestimmte Zeit aktivieren (kein Enddatum)"
              />
            </Grid>

            {/* Enddatum Kalender - nur sichtbar wenn nicht "unbestimmte Zeit" */}
            <Collapse in={!formState.loyaltyCardNoEndDate} timeout="auto" unmountOnExit sx={{ width: '100%' }}>
              <Grid item xs={12} sm={6} sx={{ pl: 3, pt: 2 }}>
                <TextField
                  fullWidth
                  label="Enddatum der Aktion"
                  type="date"
                  value={formState.loyaltyCardEndDate}
                  onChange={handleChange('loyaltyCardEndDate')}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarIcon color="action" />
                      </InputAdornment>
                    ),
                    inputProps: { min: today }
                  }}
                  InputLabelProps={{
                    shrink: true
                  }}
                  helperText="Die Treuekarten-Aktion endet an diesem Datum"
                />
              </Grid>
            </Collapse>

          </Grid>
        </Box>
        </Collapse>

        {/* ---- Treuemeilen-System (System 2) ---- */}
        <Collapse in={formState.loyaltyProgramMode === 'miles'} timeout="auto" unmountOnExit sx={{ width: '100%' }}>
        <Box sx={{ px: 3, py: 2, bgcolor: 'grey.50' }}>
          {!(formState.milesPerKm > 0 && formState.milesPerBlock > 0 && formState.milesBlockEur > 0) ? (
            <Alert severity="warning" sx={{ mb: 3 }}>
              <strong>Werte fehlen:</strong> Bitte „Meilen je km", „Meilen je Block" und „Rabatt je Block" setzen.
              Solange ein Wert 0 ist, werden keine Meilen vergeben oder eingelöst.
            </Alert>
          ) : savedProgramMode === 'miles' ? (
            <Alert severity="success" sx={{ mb: 3 }}>
              <strong>Treuemeilen aktiv:</strong> Kunden sammeln {formState.milesPerKm} Meile(n) je Kilometer.
              Je {formState.milesPerBlock} Meilen gibt es {formState.milesBlockEur}€ Rabatt auf eine künftige Fahrt
              (eine 10-km-Fahrt bringt {formState.milesPerKm * 10} Meilen).
              {globalSettings.milesStartDate && (
                <> Aktiv seit: {new Date(globalSettings.milesStartDate).toLocaleDateString('de-DE')}.</>
              )}
              {!formState.milesNoEndDate && formState.milesEndDate && (
                <> Gültig bis: {new Date(formState.milesEndDate).toLocaleDateString('de-DE')}.</>
              )}
            </Alert>
          ) : (
            <Alert severity="info" sx={{ mb: 3 }}>
              <strong>Noch nicht gespeichert.</strong> Nach dem Speichern sammeln Kunden {formState.milesPerKm} Meile(n) je Kilometer;
              je {formState.milesPerBlock} Meilen gibt es {formState.milesBlockEur}€ Rabatt.
              {savedProgramMode === 'stamp' && <> Das aktuell aktive Treuekarten-System wird dabei deaktiviert.</>}
            </Alert>
          )}

          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Meilen je Kilometer"
                type="number"
                value={formState.milesPerKm}
                onChange={handleNumberChange('milesPerKm', 0, 1000)}
                InputProps={{ inputProps: { min: 0, max: 1000 } }}
                helperText="Verdiente Meilen pro gefahrenem km"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Meilen je Rabatt-Block"
                type="number"
                value={formState.milesPerBlock}
                onChange={handleNumberChange('milesPerBlock', 0, 100000)}
                InputProps={{ inputProps: { min: 0, max: 100000 } }}
                helperText="Meilen-Schwelle für einen Rabatt"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Rabatt je Block"
                type="number"
                value={formState.milesBlockEur}
                onChange={handleNumberChange('milesBlockEur', 0, 50)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EuroIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">€</InputAdornment>
                  ),
                  inputProps: { min: 0, max: 50 }
                }}
                helperText="Rabatt je eingelöstem Block in ganzen Euro (0-50€)"
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
                Gültigkeitszeitraum
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formState.milesNoEndDate}
                    onChange={handleChange('milesNoEndDate')}
                    color="primary"
                    icon={<CalendarIcon />}
                    checkedIcon={<InfinityIcon />}
                  />
                }
                label="Für unbestimmte Zeit aktivieren (kein Enddatum)"
              />
            </Grid>

            <Collapse in={!formState.milesNoEndDate} timeout="auto" unmountOnExit sx={{ width: '100%' }}>
              <Grid item xs={12} sm={6} sx={{ pl: 3, pt: 2 }}>
                <TextField
                  fullWidth
                  label="Enddatum der Aktion"
                  type="date"
                  value={formState.milesEndDate}
                  onChange={handleChange('milesEndDate')}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarIcon color="action" />
                      </InputAdornment>
                    ),
                    inputProps: { min: today }
                  }}
                  InputLabelProps={{ shrink: true }}
                  helperText="Das Treuemeilen-System endet an diesem Datum"
                />
              </Grid>
            </Collapse>
          </Grid>
        </Box>
        </Collapse>

        {/* ---- Kein Programm aktiv ---- */}
        <Collapse in={formState.loyaltyProgramMode === 'off'} timeout="auto" unmountOnExit sx={{ width: '100%' }}>
          <Box sx={{ px: 3, py: 2, bgcolor: 'grey.50' }}>
            {savedProgramMode === 'off' ? (
              <Alert severity="info">
                Derzeit ist kein Treueprogramm aktiv. Bestehende Stände bleiben erhalten und werden
                wieder nutzbar, sobald ein Programm aktiviert wird.
              </Alert>
            ) : (
              <Alert severity="warning">
                <strong>Noch nicht gespeichert.</strong> Aktuell ist noch {savedProgramName} aktiv.
                Klicke auf „Treueprogramm speichern", um es zu deaktivieren.
              </Alert>
            )}
          </Box>
        </Collapse>

        {/* Gemeinsamer Speichern-Button */}
        <Box sx={{ px: 3, pb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? 'Wird gespeichert...' : 'Treueprogramm speichern'}
            </Button>
          </Box>
        </Box>

        </SettingsGroup>

        <SettingsGroup title="Mobile App">

        {/* Abschnitt: App-Download & QR-Code */}
        <Box sx={{ p: 2, bgcolor: 'grey.100' }}>
          <Typography variant="subtitle1" fontWeight="bold">
            App-Download & QR-Code
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Globale Einstellung (App-Store-Links & QR-Code für Kunden)
          </Typography>
        </Box>
        <Box sx={{ px: 3, py: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="iOS App Store URL"
                value={formState.appStoreUrl}
                onChange={handleChange('appStoreUrl')}
                placeholder="https://apps.apple.com/app/..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AppleIcon color="action" />
                    </InputAdornment>
                  )
                }}
                helperText="Link zur App im Apple App Store"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Google Play Store URL"
                value={formState.playStoreUrl}
                onChange={handleChange('playStoreUrl')}
                placeholder="https://play.google.com/store/apps/..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PlayStoreIcon color="action" />
                    </InputAdornment>
                  )
                }}
                helperText="Link zur App im Google Play Store"
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<QrCodeIcon />}
                  onClick={() => setQrDialogOpen(true)}
                  disabled={!formState.appStoreUrl && !formState.playStoreUrl}
                >
                  QR-Code anzeigen
                </Button>
              </Box>
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, mb: 1 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              onClick={handleSaveMobileAppLinks}
              disabled={isSaving}
            >
              {isSaving ? 'Wird gespeichert...' : 'App-Links speichern'}
            </Button>
          </Box>
        </Box>

        {/* QR-Code Dialog */}
        <MobileAppQRDialog
          open={qrDialogOpen}
          onClose={() => setQrDialogOpen(false)}
          url={`${window.location.origin}/mobile-app`}
        />

        <Divider />

        {/* Abschnitt: Telefonnummern & Datenschutz (Anruf-Buttons in den Apps) */}
        <Box sx={{ p: 2, bgcolor: 'grey.100' }}>
          <Typography variant="subtitle1" fontWeight="bold">
            <PhoneIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            Telefonnummern & Datenschutz
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Steuert, welche Telefonnummern Fahrer und Kunden in den Apps sehen und anrufen können
          </Typography>
        </Box>
        <List disablePadding>
          <ListItem
            secondaryAction={
              <Switch
                edge="end"
                checked={globalSettings.driverCanSeeCustomerPhone !== false}
                onChange={handleImmediateToggle('driverCanSeeCustomerPhone', globalSettings.driverCanSeeCustomerPhone !== false)}
                color="primary"
              />
            }
          >
            <ListItemIcon>
              <PhoneIcon />
            </ListItemIcon>
            <ListItemText
              primary="Fahrer darf Kundennummer sehen und wählen"
              secondary="Anruf-Button und Nummer des Kunden in der Fahrer-App"
            />
          </ListItem>
          <ListItem
            secondaryAction={
              <Switch
                edge="end"
                checked={globalSettings.customerCanSeeDriverPhone !== false}
                onChange={handleImmediateToggle('customerCanSeeDriverPhone', globalSettings.customerCanSeeDriverPhone !== false)}
                color="primary"
              />
            }
          >
            <ListItemIcon>
              <PhoneForwardedIcon />
            </ListItemIcon>
            <ListItemText
              primary="Kunde darf persönliche Fahrernummer sehen und wählen"
              secondary="Private Telefonnummer des Fahrers als Anrufziel in der Kunden-App"
            />
          </ListItem>
        </List>
        <Box sx={{ px: 3, pb: 1 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Ist im Fahrzeug eine Fahrzeug-Telefonnummer eingetragen (festes Smartphone), wird dem
            Kunden immer diese angezeigt — unabhängig von den Einstellungen oben.
            Anruf-Priorität in der Kunden-App: Fahrzeugnummer → persönliche Fahrernummer (falls
            erlaubt) → zentrale Telefonnummer.
          </Alert>
          <TextField
            fullWidth
            label="Zentrale Telefonnummer (Anruf-Fallback)"
            type="tel"
            value={formState.centralPhoneNumber}
            onChange={handleChange('centralPhoneNumber')}
            placeholder={brandingConfig.phone}
            helperText={`Leer lassen und speichern = Nummer aus dem Impressum übernehmen (${brandingConfig.phone})`}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, mb: 1 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              onClick={handleSaveCentralPhoneNumber}
              disabled={isSaving}
            >
              {isSaving ? 'Wird gespeichert...' : 'Telefonnummer speichern'}
            </Button>
          </Box>
        </Box>

        <Divider />

        {/* Abschnitt: Mobile App Rechtstexte */}
        <Box sx={{ p: 2, bgcolor: 'grey.100' }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Mobile App Rechtstexte
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Öffentlich zugängliche Seiten für die mobile App (z.B. für App Store Einträge)
          </Typography>
        </Box>
        <List disablePadding>
          <ListItem
            component="a"
            href="/mobile/datenschutz"
            target="_blank"
            sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
          >
            <ListItemIcon>
              <PolicyIcon />
            </ListItemIcon>
            <ListItemText
              primary="Datenschutzerklärung (Mobile App)"
              secondary={`${window.location.origin}/mobile/datenschutz`}
            />
            <OpenInNewIcon color="action" fontSize="small" />
          </ListItem>
          <Divider component="li" />
          <ListItem
            component="a"
            href="/mobile/impressum"
            target="_blank"
            sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
          >
            <ListItemIcon>
              <GavelIcon />
            </ListItemIcon>
            <ListItemText
              primary="Impressum (Mobile App)"
              secondary={`${window.location.origin}/mobile/impressum`}
            />
            <OpenInNewIcon color="action" fontSize="small" />
          </ListItem>
          <Divider component="li" />
          <ListItem
            component="a"
            href="/mobile/konto-loeschen"
            target="_blank"
            sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
          >
            <ListItemIcon>
              <PersonRemoveIcon />
            </ListItemIcon>
            <ListItemText
              primary="Kontolöschung (Google Play Pflicht)"
              secondary={`${window.location.origin}/mobile/konto-loeschen`}
            />
            <OpenInNewIcon color="action" fontSize="small" />
          </ListItem>
        </List>

        </SettingsGroup>
        </Box>

        {/* App-Version – immer sichtbar, unter allen Gruppen */}
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="caption" color="text.disabled" display="block">
            NextCab Portal v{version}
          </Typography>
          {globalSettings.currentAppVersion && (
            <Typography variant="caption" color="text.disabled" display="block">
              Mobile App v{globalSettings.currentAppVersion}
              {globalSettings.minAppVersion && ` (min. v${globalSettings.minAppVersion})`}
            </Typography>
          )}
        </Box>
      </Paper>

      {/* Festpreis Dialog */}
      <Dialog open={festpreisDialogOpen} onClose={() => setFestpreisDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingFestpreis ? 'Festpreis bearbeiten' : 'Neuer Festpreis'}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mt: 1, mb: 1 }}>
            Die Adressen dienen dem <strong>PLZ-Abgleich</strong> in der Kunden-App. Mit den
            Schaltern <em>„fixieren"</em> legen Sie fest, welches Adressfeld beim Kunden
            vorausgefüllt und <strong>nicht änderbar</strong> ist — das jeweils andere Feld
            gibt der Kunde frei ein.
          </Alert>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Start-Name"
              value={festpreisForm.start}
              onChange={(e) => setFestpreisForm(prev => ({ ...prev, start: e.target.value }))}
              required
              placeholder="z.B. HBF, Innenstadt"
              fullWidth
            />
            <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
              <PlaceAutocomplete
                label="Start-Adresse (für PLZ-Abgleich)"
                value={festpreisForm.startAdresse}
                onChange={(e) => setFestpreisForm(prev => ({ ...prev, startAdresse: e.target.value }))}
                onPlaceSelect={(formattedAddress) => {
                  if (formattedAddress) {
                    setFestpreisForm(prev => ({ ...prev, startAdresse: formattedAddress }));
                  }
                }}
              />
            </APIProvider>
            <FormControlLabel
              control={
                <Switch
                  checked={festpreisForm.festAdresseModus === 'start'}
                  onChange={(e) =>
                    setFestpreisForm(prev => ({
                      ...prev,
                      festAdresseModus: e.target.checked ? 'start' : 'ziel'
                    }))
                  }
                />
              }
              label="Startadresse fixieren (Kunde kann diese nicht ändern)"
            />
            <TextField
              label="Ziel-Name"
              value={festpreisForm.ziel}
              onChange={(e) => setFestpreisForm(prev => ({ ...prev, ziel: e.target.value }))}
              required
              placeholder="z.B. Flughafen, Nachbarstadt"
              fullWidth
            />
            <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
              <PlaceAutocomplete
                label="Ziel-Adresse (für PLZ-Abgleich)"
                value={festpreisForm.zielAdresse}
                onChange={(e) => setFestpreisForm(prev => ({ ...prev, zielAdresse: e.target.value }))}
                onPlaceSelect={(formattedAddress) => {
                  if (formattedAddress) {
                    setFestpreisForm(prev => ({ ...prev, zielAdresse: formattedAddress }));
                  }
                }}
              />
            </APIProvider>
            <FormControlLabel
              control={
                <Switch
                  checked={festpreisForm.festAdresseModus === 'ziel'}
                  onChange={(e) =>
                    setFestpreisForm(prev => ({
                      ...prev,
                      festAdresseModus: e.target.checked ? 'ziel' : 'start'
                    }))
                  }
                />
              }
              label="Zieladresse fixieren (Kunde kann diese nicht ändern)"
            />
            <TextField
              label="Preis"
              type="number"
              value={festpreisForm.preis}
              onChange={(e) => setFestpreisForm(prev => ({ ...prev, preis: e.target.value }))}
              required
              InputProps={{
                endAdornment: <InputAdornment position="end">€</InputAdornment>,
                inputProps: { min: 0, step: 0.5 }
              }}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={festpreisForm.aktiv}
                  onChange={(e) => setFestpreisForm(prev => ({ ...prev, aktiv: e.target.checked }))}
                />
              }
              label="Aktiv"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFestpreisDialogOpen(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={handleSaveFestpreis}
            disabled={!festpreisForm.start || !festpreisForm.ziel || !festpreisForm.preis || festpreiseState.isSaving}
          >
            {festpreiseState.isSaving ? 'Speichern...' : 'Speichern'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(dispatchMethodConfirm)}
        onClose={() => setDispatchMethodConfirm(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          Dispositionsmethode umstellen?
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Diese Aenderung wirkt sofort auf den laufenden Betrieb.
          </Alert>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Wechsel von <strong>{dispatchMethodLabel(dispatchMethodConfirm?.previousMethod)}</strong> auf{' '}
            <strong>{dispatchMethodLabel(dispatchMethodConfirm?.nextMethod)}</strong>.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Alle Fahrer werden automatisch abgemeldet, auch Fahrer mit aktiver Fahrt.
            Zugewiesene Fahrzeuge werden freigegeben. Laufende Sektor-Angebote werden
            abgebrochen bzw. auf die neue Methode zurueckgesetzt. Fahrer muessen sich
            danach neu anmelden und ein Fahrzeug auswaehlen.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDispatchMethodConfirm(null)}>Abbrechen</Button>
          <Button
            color="warning"
            variant="contained"
            onClick={handleConfirmDispatchMethodChange}
          >
            Trotzdem umstellen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar für Feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default SettingsPage;
